import React, { JSX } from 'react'
import { AppState, RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import Footer from 'components/Footer'
import { ChatOverlayDemo } from 'components/publicHome/ChatOverlayDemo'
import { ComingSoonStrip } from 'components/publicHome/ComingSoonStrip'
import { EmoteWallDemo } from 'components/publicHome/EmoteWallDemo'
import { HeroStage } from 'components/publicHome/HeroStage'
import { useHomeFontsReady } from 'components/publicHome/publicHomeHooks'
import { ROOT_PATH } from 'lib/paths'
import { PRIVACY_POLICY_URL } from 'lib/externalLinks'
import { PUBLIC_HOME_COPY as COPY } from 'lib/publicHomeCopy'
import { isDuplicateEmailRefusal, SIGN_IN_MESSAGES } from 'lib/signInMessages'

export const SHOW_EARLY_ACCESS_NOTE: boolean = false
export type ComingSoonMode = 'off' | 'list' | 'listAndPoll'
export const COMING_SOON_MODE = 'off' as ComingSoonMode

const STAGEPILOT_SITE_URL = 'https://www.stagepilot.com/'

const FOCUS_RING = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'

const LINK_CLASSES = `rounded-sm text-dm-text-primary underline transition-colors duration-150 hover:text-white ${FOCUS_RING}`

const CONTENT = 'mx-auto w-full max-w-6xl px-4 sm:px-6'
const EYEBROW = 'text-xs font-medium uppercase leading-4 tracking-[0.2em] text-white sm:text-sm sm:leading-[18px]'
const H2 = 'mt-3 text-[28px] font-semibold leading-8 text-dm-text-primary sm:text-[34px] sm:leading-10'
const SUBHEADER = 'mt-3 text-base leading-[22px] text-[#BFBFBF] sm:text-lg sm:leading-6'
const H3 = 'text-lg font-semibold leading-6 text-dm-text-primary sm:text-xl sm:leading-[26px]'
const BODY = 'text-[15px] leading-6 text-dm-text-secondary sm:text-base sm:leading-[26px]'

const DISPLAY_FACE = 'font-home-display-fallback font-bold uppercase tracking-tight group-data-[fonts=ready]:font-home-display group-data-[fonts=ready]:font-normal group-data-[fonts=ready]:tracking-[0.01em]'

const BUTTON = `inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition-colors duration-150 max-[399px]:w-full ${FOCUS_RING}`
const PRIMARY_BUTTON = `${BUTTON} border border-transparent bg-brand text-black hover:bg-brand/85`
const SECONDARY_BUTTON = `${BUTTON} border border-white/50 text-white hover:bg-white/10`
const HEADER_BUTTON = `min-h-11 items-center justify-center rounded-full px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors duration-150 sm:px-5 ${FOCUS_RING}`

interface WorksWithLogo {
  id: 'youtube' | 'twitch'
  href: string
  src: string
  alt: string
  width: number
  height: number
  className: string
}

const WORKS_WITH_LOGOS: readonly WorksWithLogo[] = [
  {
    id: 'youtube',
    href: 'https://www.youtube.com/',
    src: '/images/yt_logo_fullcolor_white_digital.png',
    alt: COPY.youtubeLogoAlt,
    width: 298,
    height: 100,
    className: 'h-[100px] w-auto max-w-none'
  },
  {
    id: 'twitch',
    href: 'https://www.twitch.tv/',
    src: '/images/twitch_wordmark_flat_purple.png',
    alt: COPY.twitchLogoAlt,
    width: 176,
    height: 40,
    className: 'h-10 w-auto'
  }
]

export type AuthIntent = 'signIn' | 'getStarted'

export const authRedirectOptions = (intent: AuthIntent, forceLogin = false): RedirectLoginOptions<AppState> => {
  const appState = { returnTo: ROOT_PATH }
  if (intent === 'getStarted') {
    return { appState, authorizationParams: forceLogin ? { screen_hint: 'signup', prompt: 'login' } : { screen_hint: 'signup' } }
  }
  return forceLogin ? { appState, authorizationParams: { prompt: 'login' } } : { appState }
}

const AuthButton = ({
  intent,
  testId,
  className
}: {
  intent: AuthIntent
  testId: string
  className: string
}): JSX.Element => {
  const { loginWithRedirect, error } = useAuth0()
  const forceLogin = isDuplicateEmailRefusal(error)
  return (
    <button
      type='button'
      data-testid={testId}
      data-auth-intent={intent}
      data-auth-prompt={forceLogin ? 'login' : undefined}
      onClick={() => { void loginWithRedirect(authRedirectOptions(intent, forceLogin)) }}
      className={className}
    >
      {intent === 'getStarted' ? COPY.getStarted : COPY.signIn}
    </button>
  )
}

const DuplicateEmailNotice = (): JSX.Element => {
  const { loginWithRedirect } = useAuth0()
  const words = SIGN_IN_MESSAGES.duplicateEmail
  return (
    <div
      role='alert'
      data-testid='public-home-duplicate-email'
      className='mx-auto mt-6 flex max-w-md items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-left text-sm text-amber-300'
    >
      <ExclamationTriangleIcon className='size-5 shrink-0' aria-hidden='true' />
      <div className='flex min-w-0 flex-col items-start gap-2'>
        <p className='font-semibold text-amber-200'>{words.title}</p>
        <p>{words.text}</p>
        <button
          type='button'
          data-testid='public-home-sign-in-another-way'
          data-auth-intent='signIn'
          data-auth-prompt='login'
          onClick={() => { void loginWithRedirect(authRedirectOptions('signIn', true)) }}
          className={`inline-flex min-h-11 items-center justify-center rounded-full border border-amber-300/50 px-4 py-2 text-sm font-semibold text-amber-200 transition-colors duration-150 hover:bg-amber-500/10 ${FOCUS_RING}`}
        >
          {words.anotherWay}
        </button>
      </div>
    </div>
  )
}

const FeatureText = ({
  id,
  eyebrow,
  heading,
  body,
  points,
  className = ''
}: {
  id: string
  eyebrow: string
  heading: string
  body: string
  points: readonly string[]
  className?: string
}): JSX.Element => (
  <div className={className}>
    <p className={EYEBROW}>{eyebrow}</p>
    <h2 id={id} className={H2}>{heading}</h2>
    <p className={SUBHEADER}>{body}</p>
    <ul className='mt-6 flex flex-col gap-3'>
      {points.map((point) => (
        <li key={point} className='flex items-start gap-3'>
          <CheckCircleIcon className='mt-0.5 size-5 shrink-0 text-brand' aria-hidden='true' />
          <span className={BODY}>{point}</span>
        </li>
      ))}
    </ul>
  </div>
)

export const PublicHomePage = (): JSX.Element => {
  const { error } = useAuth0()
  const duplicateEmail = isDuplicateEmailRefusal(error)
  const fontsReady = useHomeFontsReady()

  return (
    <div
      data-testid='public-home-page'
      data-fonts={fontsReady ? 'ready' : 'loading'}
      className='group flex min-h-screen flex-col overflow-x-clip bg-home-ink text-dm-text-primary data-[fonts=ready]:font-home-body'
    >

      <header className='relative z-10'>
        <div className={`${CONTENT} flex h-16 items-center justify-between gap-3`}>
          <div className='flex min-w-0 items-center gap-2'>
            <img src='/images/logo-letters.png' alt='' width={32} height={32} className='size-8 shrink-0' />
            <span className='whitespace-nowrap text-lg font-semibold text-dm-text-primary'>{COPY.headerName}</span>
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            <AuthButton
              intent='signIn'
              testId='public-home-header-sign-in'
              className={`inline-flex ${HEADER_BUTTON} border border-white/50 text-white hover:bg-white/10`}
            />
            <AuthButton
              intent='getStarted'
              testId='public-home-header-get-started'
              className={`${HEADER_BUTTON} hidden sm:inline-flex bg-brand text-black hover:bg-brand/85`}
            />
          </div>
        </div>
      </header>

      <main id='main' className='flex-1'>

        <section aria-labelledby='public-home-title' className='-mt-16 bg-home-hero pt-16'>
          <div className={`${CONTENT} pb-16 pt-10 sm:pb-24 sm:pt-16`}>
            <div className='mx-auto max-w-3xl text-center'>

              <h1 id='public-home-title'>
                <span className='block text-xl font-semibold text-dm-text-primary motion-safe:animate-home-rise sm:text-2xl lg:text-[28px]'>
                  {COPY.name}
                </span>
                {' '}
                <span className={`mt-3 block text-balance break-words text-5xl leading-[0.95] text-dm-text-primary motion-safe:animate-home-rise motion-safe:[animation-delay:80ms] sm:text-7xl lg:text-8xl ${DISPLAY_FACE}`}>
                  {COPY.heroDisplay}{' '}<span className='text-brand'>{COPY.heroDisplayAccent}</span>
                </span>
              </h1>
              <p className='mx-auto mt-6 max-w-2xl text-balance text-lg leading-[26px] text-dm-text-secondary motion-safe:animate-home-rise motion-safe:[animation-delay:160ms] sm:text-[22px] sm:leading-[30px]'>
                {COPY.heroIntro}
              </p>
              <div className='mt-8 flex flex-wrap items-center justify-center gap-3 motion-safe:animate-home-rise motion-safe:[animation-delay:240ms]'>
                <AuthButton intent='getStarted' testId='public-home-get-started' className={PRIMARY_BUTTON} />
                <AuthButton intent='signIn' testId='public-home-sign-in' className={SECONDARY_BUTTON} />
              </div>
              {SHOW_EARLY_ACCESS_NOTE && (
                <p className='mt-4 text-[13px] text-dm-text-accent'>{COPY.heroNote}</p>
              )}
              {duplicateEmail && <DuplicateEmailNotice />}
              {error != null && !duplicateEmail && (
                <p
                  role='alert'
                  data-testid='public-home-sign-in-failed'
                  className='mx-auto mt-6 flex max-w-md items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-left text-sm text-amber-300'
                >
                  <ExclamationTriangleIcon className='size-5 shrink-0' aria-hidden='true' />
                  <span>{COPY.signInFailed}</span>
                </p>
              )}
            </div>
            <HeroStage className='mt-12 motion-safe:animate-home-rise motion-safe:[animation-delay:320ms] sm:mt-16' />
          </div>
        </section>

        <section aria-labelledby='public-home-how' className='py-16 sm:py-24'>
          <div className={`${CONTENT} home-reveal`}>
            <div className='mx-auto max-w-2xl text-center'>
              <p className={EYEBROW}>{COPY.howEyebrow}</p>
              <h2 id='public-home-how' className={H2}>{COPY.howHeading}</h2>
              <p className={SUBHEADER}>{COPY.howSub}</p>
            </div>
            <ol className='mt-12 grid gap-8 sm:grid-cols-3 sm:gap-6'>
              {COPY.steps.map(({ id, title, body }, index) => (
                <li key={id} className='flex flex-col items-center text-center sm:items-start sm:text-left'>
                  <span
                    aria-hidden='true'
                    className={`flex size-10 items-center justify-center rounded-full text-2xl leading-none text-brand ring-2 ring-brand ${DISPLAY_FACE}`}
                  >
                    {index + 1}
                  </span>
                  <h3 className={`mt-4 ${H3}`}>{title}</h3>
                  <p className={`mt-2 ${BODY}`}>{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <div id='public-home-features' className='bg-home-band shadow-[0_2px_10px_1px_#0003]'>
          <section aria-labelledby='public-home-chat' className='pb-12 pt-16 sm:pb-16 sm:pt-24'>
            <div className={`${CONTENT} home-reveal grid items-center gap-10 lg:grid-cols-12 lg:gap-12`}>
              <FeatureText
                id='public-home-chat'
                eyebrow={COPY.chat.eyebrow}
                heading={COPY.chat.heading}
                body={COPY.chat.body}
                points={COPY.chat.points}
                className='lg:col-span-5'
              />
              <div className='min-w-0 lg:col-span-7'>
                <ChatOverlayDemo />
              </div>
            </div>
          </section>
          <section aria-labelledby='public-home-emotes' className='pb-16 pt-12 sm:pb-24 sm:pt-16'>
            <div className={`${CONTENT} home-reveal grid items-center gap-10 lg:grid-cols-12 lg:gap-12`}>
              <FeatureText
                id='public-home-emotes'
                eyebrow={COPY.emotes.eyebrow}
                heading={COPY.emotes.heading}
                body={COPY.emotes.body}
                points={COPY.emotes.points}
                className='lg:order-2 lg:col-span-5'
              />
              <div className='min-w-0 lg:order-1 lg:col-span-7'>
                <EmoteWallDemo />
              </div>
            </div>
          </section>
        </div>

        {COMING_SOON_MODE !== 'off' && <ComingSoonStrip withPoll={COMING_SOON_MODE === 'listAndPoll'} />}

        <section
          aria-labelledby='public-home-works-with'
          data-testid='public-home-works-with'
          className='bg-home-ink py-16 text-center sm:py-24'
        >
          <div className={CONTENT}>

            <h2 id='public-home-works-with' className='text-balance text-[28px] font-semibold leading-8 text-dm-text-primary sm:text-[34px] sm:leading-10'>
              {COPY.worksWithHeading}
            </h2>

            <ul className='mt-2 flex flex-col items-center gap-4 overflow-x-clip sm:flex-row sm:justify-center sm:gap-8'>
              {WORKS_WITH_LOGOS.map(({ id, href, src, alt, width, height, className }) => (
                <li key={id} className='flex shrink-0'>
                  <a
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    data-testid={`public-home-logo-${id}`}
                    className={`block rounded-sm ${FOCUS_RING}`}
                  >
                    <img src={src} alt={alt} width={width} height={height} loading='lazy' decoding='async' className={className} />
                  </a>
                </li>
              ))}
            </ul>

            <p className='mt-6 text-[13px] leading-5 text-dm-text-accent'>{COPY.worksWithNote}</p>
          </div>
        </section>

        <section aria-labelledby='public-home-youtube' className='pb-16 sm:pb-24'>
          <div className={CONTENT}>
            <div className='mx-auto max-w-3xl rounded-2xl border border-white/10 bg-dm-bg-secondary p-6'>
              <h2 id='public-home-youtube' className='text-xl font-semibold leading-7 text-dm-text-primary sm:text-2xl sm:leading-8'>
                {COPY.youtubeHeading}
              </h2>
              <p className={`mt-3 ${BODY}`}>{COPY.youtubeHow}</p>
              <p className={`mt-3 ${BODY}`}>{COPY.youtubeData}</p>
              <p className={`mt-3 ${BODY}`}>{COPY.youtubePreview}</p>
              <p className={`mt-3 ${BODY}`}>
                {COPY.privacyLead}{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  data-testid='public-home-privacy-inline'
                  className={LINK_CLASSES}
                >
                  {COPY.privacy}
                </a>
                {COPY.privacyTail}
              </p>
            </div>
          </div>
        </section>

        <section aria-labelledby='public-home-built-by' className='py-16 sm:py-24'>
          <div className={`${CONTENT} home-reveal`}>
            <div className='mx-auto max-w-2xl text-center'>
              <p className={EYEBROW}>{COPY.builtBy.eyebrow}</p>
              <h2 id='public-home-built-by' className={H2}>{COPY.builtBy.heading}</h2>
              <p className={SUBHEADER}>{COPY.builtBy.body}</p>
              <a
                href={STAGEPILOT_SITE_URL}
                target='_blank'
                rel='noopener noreferrer'
                data-testid='public-home-stagepilot'
                className={`mt-8 ${SECONDARY_BUTTON}`}
              >
                {COPY.builtBy.link}
              </a>
            </div>
          </div>
        </section>

        <section aria-labelledby='public-home-cta' className='bg-home-cta py-16 text-center sm:py-24'>
          <div className={`${CONTENT} home-reveal`}>
            <h2 id='public-home-cta' className='text-[28px] font-semibold leading-8 text-dm-text-primary sm:text-[34px] sm:leading-10'>
              {COPY.cta.heading}
            </h2>
            <p className={`mx-auto max-w-xl ${SUBHEADER}`}>{COPY.cta.body}</p>
            <div className='mt-8 flex flex-wrap items-center justify-center gap-3'>
              <AuthButton intent='getStarted' testId='public-home-cta-get-started' className={PRIMARY_BUTTON} />
              <AuthButton intent='signIn' testId='public-home-cta-sign-in' className={SECONDARY_BUTTON} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
